import {
  Button,
  Divider,
  Modal,
  Paper,
  Slide,
  Typography
} from '@material-ui/core'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import SaveAltIcon from '@material-ui/icons/SaveAlt'
import moment from 'moment'
import { useHistory } from 'react-router-dom'

import BookingCanceled from '../../../../assets/images/BookingCanceled.png'
import BookingConfirmed from '../../../../assets/images/BookingConfirmed.png'
import BookingFailed from '../../../../assets/images/BookingFailed.png'
import BookingModified from '../../../../assets/images/BookingModified.png'
import { PriceRow } from '../../../../components/PriceRow'
import { gregorianToJalaaliWithHourMinuteAndSecond } from '../../../../helpers/date'
import { getRoleFromStorage } from '../../../../helpers/userData'
import { getDirection, getTranslate, lang } from '../../../../localization'
import { LocalizedLink } from '../../../../routes/localizedRoutes'
import bookRoleManagement from '../../../../staticRoleManagement/bookRoleManagement'
import { ConfirmReserveResponse } from '../../../../types/ConfirmReserve'
import { PMSStatus } from '../../../../types/booking'
import { useStyles } from './styles/index.styles'

type BookingReceiptProps = {
  mode: Extract<
    PMSStatus,
    'Confirmed' | 'Cancelled' | 'ModificationReserved' | 'ReservationFailed'
  >
  open: boolean
  onClose: () => void
  data: ConfirmReserveResponse
  reservationNumber?: string
}

const BookingReceipt: React.FC<BookingReceiptProps> = ({
  mode,
  open,
  onClose,
  data,
  reservationNumber
}: BookingReceiptProps) => {
  const classes = useStyles()
  const history = useHistory()

  // Same route, same permission as "Get Confirmation" on the booking-detail page. Keep the two
  // in step: granting access in one place and not the other renders a link that 403s.
  const hasAccessToVoucher = bookRoleManagement
    .find((item) => item.route === '/booking/list/:reserveNumber/vouherPdf')
    ?.rolesListWithReadAccess?.includes(getRoleFromStorage())

  const todayDate = moment().format('YYYY-MM-DD HH:mm:ss')
  return (
    <Modal open={open} onClose={onClose} dir={getDirection()}>
      <Slide direction="down" in={open}>
        <Paper className={classes.paper}>
          <div className={classes.main}>
            <div className={classes.header}>
              <img
                src={
                  mode === 'Confirmed'
                    ? BookingConfirmed
                    : mode === 'ReservationFailed'
                    ? BookingFailed
                    : mode === 'Cancelled'
                    ? BookingCanceled
                    : BookingModified
                }
                className={classes.img}
              />
              <div className={classes.right}>
                <div className={classes.headerRow}>
                  <div>
                    <Typography className={classes.title}>
                      {mode === 'Confirmed' &&
                        getTranslate('Congratulation, Booking Confirmed')}
                      {mode === 'ReservationFailed' &&
                        getTranslate('Oop, Your Reservation Failed')}
                      {mode === 'Cancelled' &&
                        getTranslate('Your Reservation Has Been Canceled')}
                      {mode === 'ModificationReserved' &&
                        getTranslate('Your Reservation Has Been Updated')}
                    </Typography>
                    <div className={classes.textRowChip}>
                      <Typography
                        variant="h6"
                        className={classes.greyText}>{`${getTranslate(
                        'Booking ID'
                      )}: `}</Typography>
                      <Typography variant="h6" className={classes.text}>
                        {data.displayReservationNumber
                          ? data.displayReservationNumber
                          : ''}
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.date}>
                    <Typography variant="h6">
                      {`${getTranslate(
                        'Payment Date'
                      )}: ${gregorianToJalaaliWithHourMinuteAndSecond(
                        data.paymentDate ? data.paymentDate : todayDate
                      )}`}
                    </Typography>
                  </div>
                </div>
                <div className={classes.headerRow}>
                  <div className={classes.wallet}>
                    {data.wallet !== '' && (
                      <div className={classes.textRow}>
                        <Typography variant="h5" className={classes.greyText}>
                          {`${getTranslate('Wallet')}:`}
                        </Typography>
                        <Typography variant="h5" className={classes.text}>
                          {data.wallet}
                        </Typography>
                      </div>
                    )}
                    {data.providerName !== '' && (
                      <div className={classes.textRow}>
                        <Typography variant="h5" className={classes.greyText}>
                          {`${getTranslate('Provider')}:`}
                        </Typography>
                        <Typography variant="h5" className={classes.text}>
                          {data.providerName}
                        </Typography>
                      </div>
                    )}
                  </div>
                  <div className={classes.redirect}>
                    <Button
                      onClick={() => {
                        history.push(
                          `/${lang}/booking/list/${reservationNumber}/view`
                        )
                      }}
                      startIcon={<OpenInNewIcon className={classes.icon} />}>
                      <Typography variant="h5" className={classes.blueText}>
                        {getTranslate('Go to booking detail')}
                      </Typography>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {mode === 'Confirmed' ? (
              <>
                {/* <Typography variant="h5" className={classes.text}>
                  {getTranslate('Booking Transaction Numbers')}
                </Typography>
                <div className={classes.transaction}>
                  {data.roomStaysReserveDetails.map((room, index) => (
                    <div className={classes.section} key={index}>
                      <Typography variant="h6" className={classes.greyTex}>
                        {`${getTranslate('Room Stay')} ${index + 1}`}
                      </Typography>
                      <Typography variant="h6" className={classes.blackText}>
                        {room.transactionNumber ?? 0}
                      </Typography>
                    </div>
                  ))}
                </div> */}
                <Typography variant="h5" className={classes.text}>
                  {getTranslate('Booking Summary')}
                </Typography>
                <div className={classes.summary}>
                  {data.roomStaysReserveDetails.map((room, index) => (
                    <PriceRow
                      currency="IRR"
                      label={`${getTranslate('Room Stay')} ${index + 1}`}
                      value={room.totalPriceBeforeDiscount}
                    />
                  ))}
                  <PriceRow
                    currency="IRR"
                    label={`${getTranslate('Subtotal Before Discount')}`}
                    value={data.subtotalBeforeDiscount}
                    bold
                  />
                  <Divider className={classes.divider} />
                  <PriceRow
                    currency="IRR"
                    label={getTranslate('Manual Discount')}
                    value={data.totalDiscount}
                    isDiscount
                  />
                  {(data.promotionDiscount ?? 0) > 0 && (
                    <PriceRow
                      currency="IRR"
                      label={getTranslate('Promotion Discount')}
                      value={data.promotionDiscount ?? 0}
                      isDiscount
                    />
                  )}
                  <PriceRow
                    currency="IRR"
                    label={getTranslate('Additional Discount Amount')}
                    value={data.additionalDiscount}
                    isDiscount
                  />
                  <PriceRow
                    currency="IRR"
                    label={getTranslate('Subtotal After Discount')}
                    value={data.subtotalAfterDiscount}
                    bold
                  />
                  <Divider className={classes.divider} />
                  <PriceRow
                    currency="IRR"
                    label={`${getTranslate(
                      'Total Amount Payable'
                    )}\n${getTranslate('(With Tax)')}`}
                    value={data.totalPayable}
                    bold
                  />
                </div>
              </>
            ) : (
              <></>
            )}
            <div className={classes.btnContainer}>
              <Button
                variant="text"
                className={classes.btn}
                onClick={() => {
                  history.push(`/${lang}/book/select-channel`)
                }}>
                <Typography variant="h5" className={classes.redText}>
                  {getTranslate('Back to Booking')}
                </Typography>
              </Button>
              {hasAccessToVoucher && reservationNumber && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveAltIcon />}
                  className={classes.btn}
                  component={LocalizedLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  to={`/booking/list/${reservationNumber}/vouherPdf`}>
                  <Typography className={classes.whiteText}>
                    {getTranslate('Download')}
                  </Typography>
                </Button>
              )}
            </div>
          </div>
        </Paper>
      </Slide>
    </Modal>
  )
}

export default BookingReceipt
